import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity, Ambulance, BarChart3, Bell, Car, ClipboardList, CreditCard, History, Home, LayoutGrid,
  LogOut, Map, QrCode, Receipt, Settings, ShieldCheck, Siren, User, Users, Wallet,
} from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { MeApi } from "../api/services";
import { ConnectionPill, Logo } from "./common";

type Item = { to: string; label: string; icon: any; end?: boolean };

export const NAV: Record<string, Item[]> = {
  patient: [
    { to: "/patient", label: "Home", icon: Home, end: true },
    { to: "/patient/bookings", label: "Trips", icon: ClipboardList },
    { to: "/patient/book", label: "Book PTV", icon: Car },
    { to: "/patient/membership", label: "Membership", icon: Wallet },
    { to: "/profile", label: "Profile", icon: User },
  ],
  responder: [
    { to: "/responder", label: "Duty", icon: Ambulance, end: true },
    { to: "/responder/history", label: "History", icon: History },
    { to: "/notifications", label: "Alerts", icon: Bell },
    { to: "/profile", label: "Profile", icon: User },
  ],
  dispatcher: [
    { to: "/dispatch", label: "Dispatch", icon: Map, end: true },
    { to: "/dispatch/history", label: "History", icon: History },
    { to: "/notifications", label: "Alerts", icon: Bell },
    { to: "/profile", label: "Profile", icon: User },
  ],
  cashier: [
    { to: "/cashier", label: "Activation", icon: Receipt, end: true },
    { to: "/notifications", label: "Alerts", icon: Bell },
    { to: "/profile", label: "Profile", icon: User },
  ],
  admin: [
    { to: "/admin", label: "Analytics", icon: BarChart3, end: true },
    { to: "/dispatch", label: "Dispatch", icon: Map, end: true },
    { to: "/admin/users", label: "Users & roles", icon: Users },
    { to: "/admin/verification", label: "Verification", icon: ShieldCheck },
    { to: "/admin/fleet", label: "Fleet", icon: Ambulance },
    { to: "/admin/memberships", label: "Memberships", icon: Wallet },
    { to: "/admin/packages", label: "Packages", icon: QrCode },
    { to: "/admin/cards", label: "Card serials", icon: CreditCard },
    { to: "/cashier", label: "Cashier", icon: Receipt },
    { to: "/admin/transactions", label: "Transactions", icon: CreditCard },
    { to: "/admin/config", label: "Service config", icon: Settings },
    { to: "/admin/audit", label: "Audit logs", icon: Activity },
    { to: "/dispatch/history", label: "Dispatch history", icon: History },
    { to: "/profile", label: "Profile", icon: User },
  ],
};

const MOBILE_ADMIN: Item[] = [
  { to: "/admin", label: "Analytics", icon: BarChart3, end: true },
  { to: "/dispatch", label: "Dispatch", icon: Map, end: true },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/menu", label: "More", icon: LayoutGrid },
];

const slug = (s: string) => s.toLowerCase().replace(/[^a-z]+/g, "-");

function Bellbutton() {
  const { data } = useQuery({ queryKey: ["notifications"], queryFn: MeApi.notifications });
  const unread = data?.unread || 0;
  return (
    <NavLink to="/notifications" data-testid="header-notifications-button" aria-label={`Notifications, ${unread} unread`}
      className="relative flex h-11 w-11 items-center justify-center rounded-xl text-slate-600 hover:bg-slate-100">
      <Bell size={20} />
      {unread > 0 && <span data-testid="notifications-unread-count" className="absolute right-1.5 top-1.5 min-w-[18px] rounded-full bg-[#FFC400] px-1 text-center text-[10px] font-bold text-[#003F32]">{unread}</span>}
    </NavLink>
  );
}

function Sidebar({ items }: { items: Item[] }) {
  const { user, logout } = useAuth();
  return (
    <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col justify-between bg-[#024634] p-4 text-white lg:flex">
      <div>
        <div className="mb-8 px-2 pt-2"><Logo light /></div>
        <nav className="space-y-1">
          {items.map((i) => (
            <NavLink key={i.to + i.label} to={i.to} end={i.end} data-testid={`sidebar-nav-${slug(i.label)}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium ${isActive ? "bg-white text-[#024634]" : "text-emerald-50 hover:bg-white/10"}`}>
              <i.icon size={18} />{i.label}
            </NavLink>
          ))}
        </nav>
      </div>
      <div className="border-t border-white/15 pt-4">
        <p className="truncate px-2 text-sm font-semibold">{user?.name}</p>
        <p className="px-2 text-xs capitalize text-emerald-200">{user?.role}</p>
        <button onClick={logout} data-testid="sidebar-logout-button" className="mt-3 flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-emerald-50 hover:bg-white/10">
          <LogOut size={16} /> Sign out
        </button>
      </div>
    </aside>
  );
}

function BottomNav({ items, sos }: { items: Item[]; sos: boolean }) {
  const left = sos ? items.slice(0, 2) : items;
  const right = sos ? items.slice(2, 4) : [];
  const link = (i: Item) => (
    <NavLink key={i.to + i.label} to={i.to} end={i.end} data-testid={`bottom-nav-${slug(i.label)}`}
      className={({ isActive }) => `flex min-h-[52px] flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium ${isActive ? "text-[#036248]" : "text-slate-500"}`}>
      <i.icon size={21} />{i.label}
    </NavLink>
  );
  return (
    <nav className="safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur lg:hidden">
      <div className="safe-x mx-auto flex max-w-xl items-end px-2">
        {left.map(link)}
        {sos && (
          <NavLink to="/patient/emergency" data-testid="bottom-nav-sos" aria-label="Request emergency transport"
            className="relative -top-4 mx-1 flex h-16 w-16 flex-col items-center justify-center rounded-full bg-[#FFC400] text-[#003F32] shadow-lg ring-4 ring-white">
            <Siren size={24} /><span className="text-[10px] font-bold">SOS</span>
          </NavLink>
        )}
        {right.map(link)}
      </div>
    </nav>
  );
}

export function AppShell() {
  const { user } = useAuth();
  const loc = useLocation();
  if (!user) return null;
  const items = NAV[user.role];
  const mobile = user.role === "admin" ? MOBILE_ADMIN : user.role === "patient" ? items.filter((i) => i.to !== "/profile") : items;
  const wide = loc.pathname.startsWith("/dispatch") || loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/cashier");
  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar items={items} />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="safe-top sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="safe-x flex h-16 items-center justify-between gap-3 px-4 lg:px-6">
            <div className="lg:hidden"><Logo size={32} /></div>
            <p className="hidden text-sm text-slate-500 lg:block">Metro Cebu operations · Asia/Manila</p>
            <div className="flex items-center gap-1">
              <ConnectionPill />
              <Bellbutton />
              <NavLink to="/profile" data-testid="header-profile-button" aria-label="Profile" className="flex h-11 w-11 items-center justify-center rounded-xl text-slate-600 hover:bg-slate-100 lg:hidden"><User size={20} /></NavLink>
            </div>
          </div>
        </header>
        <main className={`safe-x w-full flex-1 px-4 pb-28 pt-5 lg:px-6 lg:pb-10 ${wide ? "max-w-[1600px]" : "mx-auto max-w-3xl"}`}>
          <Outlet />
        </main>
      </div>
      <BottomNav items={mobile} sos={user.role === "patient"} />
    </div>
  );
}
